/**
 * Сверка встреч с календарём устройства.
 *
 * Синхронизация двух хранилищ, которые правят независимо, — задача не про
 * «скачать и залить». Человек передвинул встречу здесь, коллега перенёс её же
 * в Google, а между этими событиями приложение не запускалось: чью версию
 * считать правдой?
 *
 * ## Откуда берётся ответ
 *
 * Android не отдаёт время правки события — только iOS. Полагаться на «у кого
 * свежее» нельзя, поэтому у каждой связанной встречи хранится отпечаток её
 * состояния на момент последней удачной сверки. Дальше всё просто:
 *
 * - отпечатки сошлись с обеих сторон — никто не менялся, делать нечего;
 * - изменилась одна сторона — её правка едет на другую;
 * - изменились обе — правка разбирается по полям.
 *
 * ## Почему по полям, а не «кто-то один побеждает»
 *
 * Целиком побеждающая сторона теряет чужую работу. Организатор перенёс встречу
 * в общем календаре, а человек здесь переименовал её и дописал заметку — при
 * победе «местного» на сервер уедет старое время, и совещание в общем календаре
 * молча переедет обратно. Поэтому берётся то поле, которое менялось: время — от
 * того, кто его двигал, название — от того, кто его правил.
 *
 * Если одно и то же поле правили с обеих сторон, побеждает календарь
 * устройства: он общий, в нём договорённость с другими людьми, а здешняя
 * запись — личная. Спорить с общей договорённостью молча нельзя.
 *
 * ## Чего сверка не делает никогда
 *
 * Однажды она стёрла человеку встречи с обеих сторон: он снял галочку с
 * календаря, события перестали приходить — и сверка приняла это за удаление в
 * телефоне и удалила их здесь. Галочку поставили обратно — и здешнее
 * «удаление» уехало в телефон. Отсюда правила:
 *
 * - записи календаря, который сейчас не читается (галочку сняли, аккаунт
 *   пропал из телефона), не сверяются вовсе — ни удаления, ни правки;
 * - удаление, которое сделала сама сверка, обратно не возвращается: запись
 *   отвязывается от события (это делает `sync-calendar.ts`);
 * - в телефоне приложение стирает только при включённой отправке;
 * - когда разом пропадает много связанных событий, это похоже на сбой, а не
 *   на решение человека, — удаление откладывается (`held`);
 * - повторяющаяся встреча в Android — одна запись на всю серию: все её дни
 *   приходят с одним номером, а правка или удаление по нему меняют серию
 *   целиком. Поэтому дни серии различаются по дате и сверяются только в одну
 *   сторону — из телефона сюда.
 *
 * Функции чистые: ни базы, ни плагина. На входе два списка, на выходе — список
 * действий, который применяет `sync-calendar.ts`.
 */

import type { CalendarEvent, Task } from '@/shared/db/schema';
import type { IsoDate, MinutesOfDay } from '@/shared/lib/datetime';

/** Событие календаря устройства в том виде, в каком оно нужно сверке. */
export interface SystemEvent {
  id: string;
  calendarId: string | null;
  title: string;
  description: string;
  location: string;
  date: IsoDate;
  startMin: MinutesOfDay;
  durationMin: number;
}

/** Поля, которые действительно синхронизируются. */
export interface SyncableFields {
  title: string;
  notes: string;
  location: string;
  date: IsoDate;
  startMin: MinutesOfDay;
  durationMin: number;
}

/**
 * Здешняя запись глазами сверки.
 *
 * Встреча и задача, поставленная на время, для календаря устройства
 * неразличимы: и то и другое — занятое время с названием. Поэтому сверка
 * работает с общим видом, а `sync-calendar.ts` знает, во что его записывать
 * обратно.
 */
export interface SyncItem {
  /** Чем запись была здесь: от этого зависит, куда вернётся правка. */
  kind: 'event' | 'task';
  id: string;
  fields: SyncableFields;
  externalId: string | null;
  externalCalendarId: string | null;
  syncedHash: string | null;
  /**
   * Записи здесь больше нет.
   *
   * Не только про удаление: задача, у которой убрали время, тоже перестаёт
   * быть записью календаря, и её пару в календаре устройства нужно убрать.
   */
  gone: boolean;
}

export type SyncAction =
  /** Завести встречу здесь: в календаре устройства она уже есть. */
  | { kind: 'createLocal'; external: SystemEvent }
  /** Поправить здешнюю запись по календарю устройства. */
  | { kind: 'updateLocal'; local: SyncItem; fields: SyncableFields; external: SystemEvent }
  /** Завести событие в календаре устройства: здесь оно уже есть. */
  | { kind: 'createExternal'; local: SyncItem }
  /** Поправить событие в календаре устройства по здешней записи. */
  | { kind: 'updateExternal'; local: SyncItem; fields: SyncableFields }
  /** Событие удалили в календаре устройства — убрать и здесь. */
  | { kind: 'deleteLocal'; local: SyncItem }
  /** Запись убрали здесь — убрать и в календаре устройства. */
  | { kind: 'deleteExternal'; local: SyncItem }
  /**
   * Отпечаток устарел, а данные совпали: сверка прошла, писать некуда.
   * Нужен, чтобы следующая сверка не считала стороны изменившимися заново.
   */
  | { kind: 'markSynced'; local: SyncItem; fields: SyncableFields };

export interface SyncPlan {
  actions: SyncAction[];
  /** Сколько полей разошлось на обеих сторонах — об этом стоит сказать. */
  conflicts: number;
  /** Сколько удалений отложено: разом пропало слишком много — похоже на сбой. */
  held: number;
}

/** День повторяющейся встречи: номер серии плюс дата. */
export function instanceId(seriesId: string, date: IsoDate): string {
  return `${seriesId}@${date}`;
}

/** Номер принадлежит дню серии — такое событие в телефоне не правится и не удаляется. */
export function isInstanceId(id: string): boolean {
  return id.includes('@');
}

/**
 * Дни серий получают собственные номера.
 *
 * Плагин отдаёт каждый день повторяющейся встречи с номером всей серии.
 * Сверка по такому номеру сводила серию к одной встрече, а та каждый раз
 * съезжала на последний день окна. Серия узнаётся по повтору номера в окне.
 */
function identifyInstances(external: readonly SystemEvent[]): SystemEvent[] {
  const count = new Map<string, number>();
  for (const event of external) count.set(event.id, (count.get(event.id) ?? 0) + 1);

  const taken = new Set<string>();
  return external.map((event) => {
    if ((count.get(event.id) ?? 0) < 2) return event;

    let id = instanceId(event.id, event.date);
    // Два дня одной серии в одну дату — редкость, но номер должен быть свой.
    for (let index = 2; taken.has(id); index += 1)
      id = `${instanceId(event.id, event.date)}#${index}`;
    taken.add(id);
    return { ...event, id };
  });
}

/**
 * Сколько исчезнувших событий уже похоже на сбой.
 *
 * Человек удаляет встречи по одной; разом пропадают они, когда календарь
 * отдал пустоту — аккаунт пересинхронизируется, провайдер сбоит. Порог
 * подобран так, чтобы удаление пары встреч проходило как обычно.
 */
const SUSPICIOUS_REMOVALS = 5;
const SUSPICIOUS_SHARE = 0.3;

export function fieldsOfLocal(event: CalendarEvent): SyncableFields {
  return {
    title: event.title,
    notes: event.notes,
    location: event.location,
    date: event.date,
    startMin: event.startMin,
    durationMin: event.durationMin,
  };
}

/** Встреча — запись календаря всегда. */
export function itemOfEvent(event: CalendarEvent): SyncItem {
  return {
    kind: 'event',
    id: event.id,
    fields: fieldsOfLocal(event),
    externalId: event.externalId,
    externalCalendarId: event.externalCalendarId,
    syncedHash: event.syncedHash,
    gone: event.deletedAt !== null,
  };
}

/**
 * Задача — запись календаря, только когда у неё есть день и время.
 *
 * Задача без времени видна в списке дня, но места в сетке не занимает, и в
 * чужом календаре ей делать нечего. Если время убрали, задача считается
 * исчезнувшей: её пара в календаре устройства должна уйти, иначе там навсегда
 * останется занятое время, которого больше нет.
 */
export function itemOfTask(task: Task): SyncItem {
  const scheduled = task.plannedDate !== null && task.plannedStartMin !== null;

  return {
    kind: 'task',
    id: task.id,
    fields: {
      title: task.title,
      notes: task.notes,
      location: '',
      date: task.plannedDate ?? '1970-01-01',
      startMin: task.plannedStartMin ?? 0,
      durationMin: Math.max(5, task.estimateMin),
    },
    externalId: task.externalId,
    externalCalendarId: task.externalCalendarId,
    syncedHash: task.syncedHash,
    gone: task.deletedAt !== null || !scheduled,
  };
}

export function fieldsOfExternal(event: SystemEvent): SyncableFields {
  return {
    title: event.title,
    notes: event.description,
    location: event.location,
    date: event.date,
    startMin: event.startMin,
    durationMin: event.durationMin,
  };
}

/**
 * Отпечаток синхронизируемых полей.
 *
 * Обычная свёртка: задача — заметить изменение, а не защититься от подделки.
 * Разделитель между полями обязателен, иначе «Обед» + «в 13» и «Обед в 13» + ''
 * дали бы одинаковый отпечаток.
 */
export function fingerprint(fields: SyncableFields): string {
  const text = [
    fields.title,
    fields.notes,
    fields.location,
    fields.date,
    fields.startMin,
    fields.durationMin,
  ].join(' ');

  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export interface SyncInput {
  /** Записи приложения в сверяемом окне — включая мягко удалённые. */
  local: SyncItem[];
  /** События календаря устройства в том же окне. */
  external: SystemEvent[];
  /** Отправлять ли здешние встречи в календарь устройства. */
  push: boolean;
  /**
   * Календари, события которых прочитаны в этот раз. Связанные записи других
   * календарей не сверяются: их событий нет в списке не потому, что их
   * удалили, а потому, что их не читали.
   */
  readCalendars: readonly string[];
  /** Откуда заводить новые встречи здесь — выбранные человеком календари. */
  sources: readonly string[];
}

export function planCalendarSync({
  local,
  external,
  push,
  readCalendars,
  sources,
}: SyncInput): SyncPlan {
  const actions: SyncAction[] = [];
  const removals: SyncAction[] = [];
  let conflicts = 0;
  let linkedAlive = 0;

  const read = new Set(readCalendars);
  const instances = identifyInstances(external);
  const externalById = new Map(instances.map((event) => [event.id, event]));
  const seriesIds = new Set(
    instances.filter((event) => isInstanceId(event.id)).map((event) => event.id.split('@')[0]),
  );
  const linkedIds = new Set<string>();

  for (const event of local) {
    const externalId = event.externalId;

    if (externalId === null) {
      // Запись заведена здесь и в календаре устройства ещё не появлялась.
      if (push && !event.gone) actions.push({ kind: 'createExternal', local: event });
      continue;
    }

    linkedIds.add(externalId);

    // Календарь сейчас не читается — его записи ждут, не трогаем их.
    if (event.externalCalendarId === null || !read.has(event.externalCalendarId)) continue;

    // Прежняя сверка заводила на серию одну встречу с номером серии —
    // связываем её с днём серии, на который она приходится.
    const counterpart =
      externalById.get(externalId) ??
      (seriesIds.has(externalId)
        ? externalById.get(instanceId(externalId, event.fields.date))
        : undefined);
    if (counterpart) linkedIds.add(counterpart.id);

    // День серии правится и удаляется только здесь: в телефоне это изменило
    // бы всю серию.
    const readOnly = isInstanceId(counterpart?.id ?? externalId) || seriesIds.has(externalId);

    if (event.gone) {
      // Здесь удалили — удаляем и там, если отправка разрешена.
      if (counterpart && push && !readOnly) actions.push({ kind: 'deleteExternal', local: event });
      continue;
    }

    linkedAlive += 1;

    if (!counterpart) {
      // Связанное событие пропало из календаря устройства: его удалили.
      removals.push({ kind: 'deleteLocal', local: event });
      continue;
    }

    const relinked = counterpart.id !== externalId;

    const shadow = event.syncedHash;
    const localFields = event.fields;
    const externalFields = fieldsOfExternal(counterpart);

    const localChanged = fingerprint(localFields) !== shadow;
    const externalChanged = fingerprint(externalFields) !== shadow;

    if (!localChanged && !externalChanged) {
      if (relinked) {
        actions.push({
          kind: 'updateLocal',
          local: event,
          fields: externalFields,
          external: counterpart,
        });
      }
      continue;
    }

    if (localChanged && !externalChanged) {
      if (push && !readOnly) {
        actions.push({ kind: 'updateExternal', local: event, fields: localFields });
      }
      continue;
    }

    if (!localChanged && externalChanged) {
      actions.push({
        kind: 'updateLocal',
        local: event,
        fields: externalFields,
        external: counterpart,
      });
      continue;
    }

    // Менялись обе стороны: собираем запись по полям.
    const merged = mergeFields(localFields, externalFields);
    conflicts += merged.conflicts;

    actions.push({
      kind: 'updateLocal',
      local: event,
      fields: merged.fields,
      external: counterpart,
    });
    if (push && !readOnly) {
      actions.push({ kind: 'updateExternal', local: event, fields: merged.fields });
    }
  }

  const imported = new Set(sources);
  for (const event of instances) {
    if (linkedIds.has(event.id)) continue;
    if (event.calendarId === null || !imported.has(event.calendarId)) continue;
    actions.push({ kind: 'createLocal', external: event });
  }

  // Разом пропало много связанных событий — удалять не спешим.
  const suspicious =
    removals.length >= SUSPICIOUS_REMOVALS && removals.length > linkedAlive * SUSPICIOUS_SHARE;
  if (!suspicious) actions.push(...removals);

  return { actions, conflicts, held: suspicious ? removals.length : 0 };
}

/**
 * Слияние по полям при правке с обеих сторон.
 *
 * Отпечатка отдельных полей у нас нет — только общий, — поэтому «кто менял
 * это поле» вычислить нельзя. Значит, спор решается правилом: где значения
 * разошлись, берётся календарь устройства. Он общий, в нём договорённость с
 * другими людьми; здешняя запись личная, и переписывать ею чужой календарь
 * молча нельзя.
 *
 * Совпавшие поля не в счёт: они и не спорят.
 */
function mergeFields(
  local: SyncableFields,
  external: SyncableFields,
): { fields: SyncableFields; conflicts: number } {
  const keys = Object.keys(local) as (keyof SyncableFields)[];
  const conflicts = keys.filter((key) => local[key] !== external[key]).length;

  return { fields: external, conflicts };
}
